import Modal from "@/components/atoms/Modal";
import CommercialBookingFields, {
  CommercialBookingSelection,
} from "@/components/molecules/calendar/CommercialBookingFields";
import Select, { SelectPaginateAsync } from "@/components/atoms/Select";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import useLoadOptions from "@/hooks/useLoadOptions";
import { IFilters } from "@/interfaces/Fetch";
import Meeting, {
  COMPANY_MEETING_TYPE,
  MeetingForm,
} from "@/interfaces/Meeting";
import { IModalMethods } from "@/interfaces/Modal";
import {
  useLazyGetCompanyMeetingQuery,
  useUpdateCompanyMeetingMutation,
} from "@/services/companies/meetings";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { DateTime } from "luxon";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useSelector } from "react-redux";
import { ToastContentProps, toast } from "react-toastify";

const UpdateCompanyMeetingModal: ForwardRefRenderFunction<
  IModalMethods,
  { companyId: string }
> = ({ companyId }, ref) => {
  const [id, setId] = useState<string>();
  const [meeting, setMeeting] = useState<Meeting>();
  const [loadError, setLoadError] = useState(false);
  const [moving, setMoving] = useState(false);
  const [booking, setBooking] = useState<
    CommercialBookingSelection & {
      companyId?: string;
      meetingId?: string;
    }
  >({ startAt: "", ready: false });
  const [bookingRefresh, setBookingRefresh] = useState(0);
  const [bookingConflict, setBookingConflict] = useState("");
  const loadVersion = useRef(0);
  const [getCompanyMeetingLazy] = useLazyGetCompanyMeetingQuery();
  const [updateCompanyMeetingAsync, { isLoading: isUpdating }] =
    useUpdateCompanyMeetingMutation();
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const user = useSelector(selectCurrentUser);
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<MeetingForm>();

  const { loadCompanyStatusesOptions, loadCompanyCoursesOptions } =
    useLoadOptions();
  const isCommercial = meeting?.calendarType === "commercial";
  const loaded =
    !!meeting && meeting.id === id && meeting.companyId === companyId;
  const bookingReady =
    booking.ready &&
    booking.companyId === companyId &&
    booking.meetingId === id;
  const onBookingChange = useCallback(
    (selection: CommercialBookingSelection) => {
      setBooking({ ...selection, companyId, meetingId: id });
      setValue("startAt", selection.startAt);
    },
    [companyId, id, setValue],
  );

  const resetModal = () => {
    loadVersion.current += 1;
    setId(undefined);
    setMeeting(undefined);
    setLoadError(false);
    setMoving(false);
    setBooking({ startAt: "", ready: false });
    setBookingConflict("");
    reset({});
  };

  const defaultAdditional = useRef<IFilters>({
    pageIndex: 0,
  }).current;

  const showOrHiddenModal = async (id?: string) => {
    resetModal();
    const version = loadVersion.current;
    try {
      setId(id);
      ModalRef.current?.showOrHiddenModal();
      if (id) {
        const data = await getCompanyMeetingLazy([companyId, id]).unwrap();
        if (version !== loadVersion.current) return;
        if (data.id !== id || data.companyId !== companyId) {
          setLoadError(true);
          return;
        }
        reset({
          startAt: DateTime.fromISO(data.startAt, {
            zone:
              data.calendarType === "commercial" ? "Europe/Paris" : undefined,
          })
            .setZone("Europe/Paris")
            .toFormat("yyyy-MM-dd'T'HH:mm"),
          status: data.company?.status,
          companyCourses: data.companyCourses,
          type:
            COMPANY_MEETING_TYPE.find((type) => type.value === data.type) ??
            COMPANY_MEETING_TYPE[0],
        });
        setMeeting(data);
      }
    } catch (e) {
      if (version === loadVersion.current) setLoadError(true);
    }
  };

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<MeetingForm> = ({
    type,
    status,
    companyCourses,
    startAt,
    calendarType: _calendarType,
    ...data
  }) => {
    if (id && loaded) {
      if (
        isCommercial &&
        moving &&
        (!bookingReady || booking.startAt !== startAt)
      ) {
        toast.error("Choisissez un créneau commercial disponible.");
        return;
      }
      const version = loadVersion.current;
      const request = updateCompanyMeetingAsync([
        companyId,
        id,
        {
          ...data,
          ...(!isCommercial
            ? { startAt }
            : moving
              ? {
                  startAt,
                  bookingProtocol: 2 as const,
                  proposalToken: booking.proposalToken,
                }
              : {}),
          ...(isCommercial
            ? {
                companyCourseIds:
                  companyCourses?.map((course) => course.id) ?? [],
              }
            : {}),
          statusId: status?.id,
          type: type?.value ?? COMPANY_MEETING_TYPE[0].value,
        },
      ])
        .unwrap()
        .catch((error: FetchBaseQueryError) => {
          if (
            version === loadVersion.current &&
            isCommercial &&
            moving &&
            error.status === 409
          ) {
            setBooking({ startAt: "", ready: false });
            setValue("startAt", "");
            setBookingConflict(
              (error.data as { message?: string })?.message ??
                "La proposition a changé. Choisissez un nouveau créneau et confirmez à nouveau.",
            );
            setBookingRefresh((value) => value + 1);
          }
          throw error;
        });
      toast.promise(request, {
        pending: "Modification du rendez-vous en cours...",
        success: {
          render() {
            if (version === loadVersion.current) showOrHiddenModal();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                Le rendez-vous a bien été modifié 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 409)
              return (
                (data.data as { message?: string })?.message ??
                "La proposition a changé. Choisissez un nouveau créneau."
              );
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      });
    } else {
      toast.error(INVALID_FORM_MESSAGE);
    }
  };

  const onError: SubmitErrorHandler<MeetingForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title="Modifier un rendez-vous"
      requiredLabel
      isLoading={
        !loaded || isUpdating || (isCommercial && moving && !bookingReady)
      }
      onHide={resetModal}
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      {!loaded ? (
        <p role={loadError ? "alert" : "status"}>
          {loadError
            ? "Impossible de charger le rendez-vous. Fermez puis rouvrez la fenêtre pour réessayer."
            : "Chargement du rendez-vous…"}
        </p>
      ) : (
        <Row className="gx-3">
          {isCommercial && (
            <Col lg={12}>
              <Form.Group className="mb-3" controlId="update-meeting-company">
                <Form.Label>Entreprise</Form.Label>
                <Form.Control value={meeting.company?.name ?? ""} readOnly />
              </Form.Group>
            </Col>
          )}
          {checkIfUserHasRoles(user, ["ADMIN", "AE", "CSM"]) && (
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Statut <span className="text-danger">*</span> :
                </Form.Label>
                <Controller
                  control={control}
                  name="status"
                  rules={{ required: "Le statut est obligatoire" }}
                  render={({ field }) => (
                    <SelectPaginateAsync
                      loadOptions={loadCompanyStatusesOptions}
                      value={field.value}
                      defaultOptions
                      additional={defaultAdditional}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id.toString()}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Form.Group>
            </Col>
          )}
          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Type de rendez-vous <span className="text-danger">*</span>
              </Form.Label>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <Select
                    options={COMPANY_MEETING_TYPE}
                    defaultValue={COMPANY_MEETING_TYPE[0]}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Form.Group>
          </Col>
          {isCommercial ? (
            <Col lg={12}>
              <p>
                Date actuelle :{" "}
                {DateTime.fromISO(meeting.startAt, { zone: "Europe/Paris" })
                  .setZone("Europe/Paris")
                  .setLocale("fr")
                  .toFormat("dd LLL yyyy HH:mm")}
              </p>
              <Form.Check
                type="switch"
                id="move-commercial-meeting"
                label="Déplacer le rendez-vous"
                checked={moving}
                onChange={(event) => {
                  setMoving(event.target.checked);
                  setBooking({ startAt: "", ready: false });
                  setValue("startAt", "");
                  setBookingConflict("");
                }}
              />
              {moving && (
                <>
                  {bookingConflict && (
                    <p role="alert" className="text-warning">
                      {bookingConflict}
                    </p>
                  )}
                  <CommercialBookingFields
                    key={meeting.id}
                    companyId={companyId}
                    meetingId={meeting.id}
                    initialStartAt={meeting.startAt}
                    onChange={onBookingChange}
                    refreshKey={bookingRefresh}
                  />
                </>
              )}
            </Col>
          ) : (
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Date et heure <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="datetime-local"
                  {...register(`startAt`, {
                    validate: (value) => {
                      if (DateTime.fromISO(value) < DateTime.now()) {
                        return "La date de début doit être supérieure à la date actuelle";
                      }
                    },
                    required: "La date de début est obligatoire",
                  })}
                  isInvalid={!!errors.startAt}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.startAt?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          )}
          {isCommercial && (
            <Col lg={12}>
              <Form.Group className="mb-3">
                <Form.Label>Formations liées (optionnel)</Form.Label>
                <Controller
                  control={control}
                  name="companyCourses"
                  render={({ field }) => (
                    <SelectPaginateAsync
                      loadOptions={loadCompanyCoursesOptions}
                      additional={{ pageIndex: 0, companyId }}
                      cacheUniqs={[companyId]}
                      getOptionLabel={(option) =>
                        option.course?.name ?? "Formation"
                      }
                      getOptionValue={(option) => option.id.toString()}
                      value={field.value}
                      onChange={field.onChange}
                      isClearable
                      isMulti
                    />
                  )}
                />
              </Form.Group>
            </Col>
          )}
        </Row>
      )}
    </Modal>
  );
};

export default forwardRef(UpdateCompanyMeetingModal);
